import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react'
import CommonModal from '../../../../shared/components/modal/common-modal'
import { useTranslation } from 'react-i18next'
import CommonTable from '../../../../shared/components/common/table'
import { Button, Flex, notification, Row, Space, Spin, Typography } from 'antd'
import dayjs from 'dayjs'
import { FORMAT_DATE_TIME } from '../../../../shared/constants/app.constants'
import saveAs from 'file-saver'
import './AccountSetupUpload.scss'
import { DownloadAccountErrorDetailIcon } from '../../../../../assets/icons'
import { colorBgError, colorBgErrorBold } from '../../../../themes/primaryThemeDefault'
import { getErrorMessage, IResImportFile } from './useAccountSetupUpload'
import { StatusTag } from 'src/app/features/feature-transaction-rules/ach-exception-rules/ach-upload-multiple-template/StatusTag'
import { SYSTEM_MESSAGE_IDS } from 'src/app/shared/constants/systemMessages.constants'
import { LoadingOutlined } from '@ant-design/icons'
import { TableColumnDefs } from 'src/app/shared/components/common/new-table/table.model'
interface ErrorDetailsUploadProps {
  onClose?: () => any
  ref?: any
}

const ErrorDetailsUpload: React.FC<ErrorDetailsUploadProps> = forwardRef(({ onClose }, ref) => {
  const { t } = useTranslation()
  const [api, contextHolder] = notification.useNotification()
  const [isShowModal, setIsShowModal] = useState<boolean>(false)
  const [resImportFile, setResImportFile] = useState<IResImportFile>()

  const showExporting = useCallback(() => {
    api.info({
      message: t('common:exporting'),
      placement: 'topRight',
      duration: 3000,
      icon: (
        <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} size='small' style={{ fontSize: '20px' }} />
      ),
      style: {
        backgroundColor: '#F5FBFF',
        borderRadius: '8px',
        padding: '12px 16px',
        border: '1px solid #43BBFF',
        boxShadow: 'none',
        height: '50px',
        width: '250px',
        display: 'flex',
        alignItems: 'stretch',
      },
    })
  }, [api, t])

  const showExportingError = useCallback(
    (error: any) => {
      api.error({
        message: t(`SystemMessages:${SYSTEM_MESSAGE_IDS.GENERAL_ERROR_MESSAGE}`),
        placement: 'topRight',
        duration: 3,
        closeIcon: <></>,
      })
    },
    [api, t],
  )

  const getHeaderName = useCallback(
    (field: string): string => {
      return t(`AccountSetup:uploadFile.errorDetails.tableCol.${field}`)
    },
    [t],
  )
  const columnDefs: TableColumnDefs[] = useMemo(
    (): TableColumnDefs[] => [
      {
        colId: 'importType',
        field: 'importType',
        headerName: getHeaderName('importType'),
        hide: false,
        defaultSelect: true,
        width: 124,
        minWidth: 124,
      },
      {
        colId: 'fileName',
        field: 'fileName',
        headerName: getHeaderName('fileName'),
        hide: false,
        defaultSelect: true,
        width: 124,
        minWidth: 124,
      },
      {
        colId: 'inputDate',
        field: 'inputDate',
        headerName: getHeaderName('inputDate'),
        hide: false,
        defaultSelect: true,
        cellRenderer: (data: any) => {
          return (
            <Typography.Text
              style={{
                fontSize: 14,
                fontFamily: 'MyriadPro',
              }}
            >
              {data.data.inputDate}
            </Typography.Text>
          )
        },

        width: 124,
        minWidth: 124,
      },
      {
        colId: 'status',
        field: 'status',
        headerName: getHeaderName('status'),
        hide: false,
        defaultSelect: true,
        cellRenderer: (data: any) => {
          return <StatusTag status={data.data.status} />
        },

        width: 124,
        minWidth: 124,
      },
      {
        colId: 'processedItems',
        field: 'processedItems',
        headerName: getHeaderName('processedItems'),
        hide: false,
        defaultSelect: true,
        width: 124,
        minWidth: 124,
      },
      {
        colId: 'rejectedItems',
        field: 'rejectedItems',
        headerName: getHeaderName('rejectedItems'),
        hide: false,
        defaultSelect: true,
        width: 124,
        minWidth: 124,
      },
      {
        colId: 'uploadedBy',
        field: 'uploadedBy',
        headerName: getHeaderName('uploadedBy'),
        hide: false,
        defaultSelect: true,
        width: 124,
        minWidth: 124,
      },
    ],
    [getHeaderName],
  )

  const handleCloseModal = () => {
    setIsShowModal(false)
    onClose && onClose()
  }

  const handleOpenModal = (data: IResImportFile) => {
    setIsShowModal(true)
    setResImportFile(data)
  }

  const getRowTable = useCallback(() => {
    return [
      {
        importType: resImportFile?.importType, // getImportTypeText(t, resImportFile?.importType ?? 0),
        fileName: resImportFile?.fileName,
        inputDate: dayjs(resImportFile?.issuedDate).format(FORMAT_DATE_TIME),
        status: resImportFile?.status,
        processedItems: resImportFile?.processedCount ?? 0,
        rejectedItems: resImportFile?.rejectedCount ?? 0,
        uploadedBy: resImportFile?.uploadedBy,
      },
    ]
  }, [resImportFile])

  const handleDownload = useCallback(async () => {
    try {
      showExporting()
      const data = await (await fetch(resImportFile?.uploadedPath ?? '')).arrayBuffer()
      const dataBlob = new Blob([new Uint8Array(data)])
      saveAs(dataBlob, resImportFile?.fileName)
      if (data) {
        api.destroy()
      }
    } catch (error) {
      // Follow show download toast message logic in common table
      api.destroy()
      setTimeout(() => {
        showExportingError(error)
      }, 500)
    }
  }, [api, showExporting, showExportingError, resImportFile])

  useImperativeHandle(
    ref,
    () => {
      return {
        openModal(data: IResImportFile) {
          handleOpenModal(data)
        },
        closeModal() {
          handleCloseModal()
        },
      }
    },
    [],
  )

  return (
    <CommonModal
      title={t('AccountSetup:uploadFile.errorDetails.errorDetails')}
      open={isShowModal}
      cancelText={t('AccountSetup:uploadFile.errorDetails.close')}
      cancelButtonProps={{ style: { width: 102, fontWeight: 600 }, size: 'middle' }}
      onCancel={handleCloseModal}
      onClose={handleCloseModal}
      okButtonProps={{
        style: { display: 'none' },
      }}
      width={960}
      maskClosable={false}
      destroyOnClose
    >
      {contextHolder}
      <Flex justify={'flex-end'}>
        <Button icon={<DownloadAccountErrorDetailIcon />} style={{ fontWeight: 600 }} onClick={handleDownload}>
          {t('AccountSetup:uploadFile.errorDetails.downloadFile')}
        </Button>
      </Flex>

      <div className={'error-details-upload-account'} style={{ width: '100%' }}>
        <CommonTable
          columnDefs={columnDefs}
          rowData={getRowTable()}
          disableTableControl
          displayActionColumn={false}
          handleChooseColumn={async () => {}}
          handleSearch={async () => {}}
          autoSort={false}
          getHeaderName={null}
        />
      </div>

      <Flex justify={'space-between'} align={'center'} style={{ height: 32, marginBottom: 12 }}>
        <Typography.Text style={{ fontSize: 14, fontWeight: 700 }}>
          {t('AccountSetup:uploadFile.errorDetails.errorMessages')}
        </Typography.Text>
      </Flex>

      <Space size={8} direction={'vertical'} style={{ width: '100%', height: 265, overflow: 'scroll' }}>
        {resImportFile?.rejectedItems.map((value) => {
          return (
            <Row
              key={value}
              style={{
                border: `1px solid ${colorBgErrorBold}`,
                width: '100%',
                padding: '8px 12px',
                borderRadius: 12,
                backgroundColor: colorBgError,
              }}
            >
              <Typography.Text style={{ fontSize: 14 }}> {getErrorMessage(value, t)}</Typography.Text>
            </Row>
          )
        })}
      </Space>
    </CommonModal>
  )
})

export default ErrorDetailsUpload
